import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import radioCheck from "assets/svg/radio-check.svg";
import DateTimePicker from "../../components/DateTimePicker";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  generateOppositionFileRejoinderAffidavitPresignedUrls,
  getOppositionFileRejoinderAffidavit,
  saveOppositionFileRejoinderAffidavit,
  type OppositionFileRejoinderAffidavitItem,
} from "api/opposition";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const OPPOSITION_FILE_REJOINDER_FORM_ID =
  "opposition-file-rejoinder-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

function parseISODate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

function toISODateString(date: Date | null): string | null {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

type RowState = {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfFiling: Date | null;
  pdfFileKey: string | null;
  pdfFilename: string | null;
  pdfDownloadUrl: string | null;
  pendingPdfFile: File | null;
  localPdfPreviewUrl: string | null;
};

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionFileRejoinderView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<RowState[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionFileRejoinderAffidavit(leadId)
      .then((data) => {
        if (cancelled) return;
        setRows(
          data.conflictingMarks.map(
            (m: OppositionFileRejoinderAffidavitItem) => ({
              id: m.id,
              conflictingMark: m.conflictingMark,
              applicationNo: m.applicationNo,
              dateOfFiling: parseISODate(m.dateOfFilingRejoinder),
              pdfFileKey: m.rejoinderAffidavitFileKey,
              pdfFilename: m.rejoinderAffidavitFilename,
              pdfDownloadUrl: m.rejoinderAffidavitDownloadUrl,
              pendingPdfFile: null,
              localPdfPreviewUrl: null,
            })
          )
        );
        setActiveIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load File Rejoinder"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(
    () => () => {
      rows.forEach((r) => {
        if (r.localPdfPreviewUrl) URL.revokeObjectURL(r.localPdfPreviewUrl);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const activeRow = rows[activeIdx] ?? null;

  const updateActive = (patch: Partial<RowState>) => {
    if (!activeRow) return;
    setRows((prev) =>
      prev.map((r) => (r.id === activeRow.id ? { ...r, ...patch } : r))
    );
  };

  const handlePdfChosen = (file: File | null) => {
    if (!activeRow) return;
    if (activeRow.localPdfPreviewUrl) URL.revokeObjectURL(activeRow.localPdfPreviewUrl);
    if (!file) {
      updateActive({ pendingPdfFile: null, localPdfPreviewUrl: null });
      return;
    }
    updateActive({
      pendingPdfFile: file,
      localPdfPreviewUrl: URL.createObjectURL(file),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (rows.length === 0) {
      toast.error("No eligible applications.");
      return;
    }
    setSaving(true);
    try {
      const uploads = rows
        .filter((r) => r.pendingPdfFile)
        .map((r) => ({ rowId: r.id, file: r.pendingPdfFile! }));
      const keyMap: Record<string, { key: string; filename: string }> = {};
      if (uploads.length > 0) {
        const presigned =
          await generateOppositionFileRejoinderAffidavitPresignedUrls(
            leadId,
            uploads.map((u) => ({
              filename: u.file.name,
              mimeType: u.file.type || "application/pdf",
            }))
          );
        if (presigned.length !== uploads.length) {
          throw new Error("Failed to generate upload URLs");
        }
        await Promise.all(
          uploads.map(async (u, i) => {
            const target = presigned[i];
            const res = await fetch(target.url, {
              method: "PUT",
              body: u.file,
              headers: { "Content-Type": u.file.type || "application/pdf" },
            });
            if (!res.ok) throw new Error(`Failed to upload ${u.file.name}`);
            keyMap[u.rowId] = { key: target.key, filename: u.file.name };
          })
        );
      }
      const saved = await saveOppositionFileRejoinderAffidavit(leadId, {
        conflictingMarks: rows.map((r) => {
          const upload = keyMap[r.id];
          return {
            conflictingMarkId: r.id,
            dateOfFilingRejoinder: toISODateString(r.dateOfFiling),
            rejoinderAffidavitFileKey: upload?.key ?? r.pdfFileKey,
            rejoinderAffidavitFilename: upload?.filename ?? r.pdfFilename,
          };
        }),
      });
      setRows(
        saved.conflictingMarks.map((m) => ({
          id: m.id,
          conflictingMark: m.conflictingMark,
          applicationNo: m.applicationNo,
          dateOfFiling: parseISODate(m.dateOfFilingRejoinder),
          pdfFileKey: m.rejoinderAffidavitFileKey,
          pdfFilename: m.rejoinderAffidavitFilename,
          pdfDownloadUrl: m.rejoinderAffidavitDownloadUrl,
          pendingPdfFile: null,
          localPdfPreviewUrl: null,
        }))
      );
      toast.success("File Rejoinder saved.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save File Rejoinder"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!activeRow?.pdfDownloadUrl) return;
    try {
      const result = await downloadFileFromUrlWithFallback(
        activeRow.pdfDownloadUrl,
        activeRow.pdfFilename ?? "rejoinder.pdf"
      );
      if (result === "local") toast.success("PDF saved to your device.");
      else toast.info("Opened the PDF in a new tab.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  };

  const handlePreview = () => {
    const url = activeRow?.localPdfPreviewUrl ?? activeRow?.pdfDownloadUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (rows.length === 0 || !activeRow) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No applications on an ACTIVE Rejoinder invoice yet.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_FILE_REJOINDER_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {rows.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {rows.map((r, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(r.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Date of Filing Rejoinder</label>
          <DateTimePicker
            value={activeRow.dateOfFiling}
            onChange={(d) => updateActive({ dateOfFiling: d })}
            showTime={false}
            placeholder="Select Date"
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className={LABEL_CLASS}>Filed Rejoinder Affidavit (PDF)</label>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2.5">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              handlePdfChosen(f);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={saving}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
          >
            {activeRow.pendingPdfFile || activeRow.pdfFilename
              ? "Replace PDF"
              : "Upload PDF"}
          </button>
          {(activeRow.pendingPdfFile || activeRow.pdfDownloadUrl) && (
            <button
              type="button"
              onClick={handlePreview}
              disabled={saving}
              className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
            >
              Preview
            </button>
          )}
          {activeRow.pdfDownloadUrl && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={saving}
              className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
            >
              Download
            </button>
          )}
        </div>
        {(activeRow.pendingPdfFile || activeRow.pdfFilename) && (
          <span className="text-[11px] leading-4 text-[#6D6D6D]">
            {activeRow.pendingPdfFile
              ? `New: ${activeRow.pendingPdfFile.name}`
              : activeRow.pdfFilename}
          </span>
        )}
      </div>
    </form>
  );
}
